/**
 * OfficerWeekView — one officer's week across every job.
 *
 * A manager scheduling a site sees their own job. This answers the other
 * question: what is this person's whole week, including the jobs I do not
 * schedule? Hours from a job outside this caller's scheduling view still count
 * toward the officer's total - hiding them would make the total wrong - so
 * they are named as other work rather than linked.
 *
 * Hours only, for every viewer. No rate reaches this screen: a pay rate is
 * shown on a shift row in the grid at schedule close, which is what the
 * 2026-09-24 decision covers, and an officer's week across jobs is a pay
 * report the moment a rate appears on it.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { schedulingAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Moon } from 'lucide-react';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hhmm = (v) => (v || '').slice(0, 5);
const shiftWeeks = (weekStart, n) => {
  const d = new Date(`${weekStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n * 7);
  return d.toISOString().slice(0, 10);
};

export default function OfficerWeekView() {
  const { employeeId, weekStart } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    schedulingAPI.officerWeek({ employee_id: employeeId, week_start_date: weekStart })
      .then(({ data: d }) => { setData(d); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load that week'))
      .finally(() => setLoading(false));
  }, [employeeId, weekStart]);

  const name = data?.employee
    ? `${data.employee.last_name || ''}${data.employee.first_name ? `, ${data.employee.first_name}` : ''}`
    : 'Officer';

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="officer-week-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
            data-testid="officer-week-back" className="-ml-2 mb-1">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Button>
          <h1 className="text-3xl sm:text-4xl font-semibold text-fg tracking-tight">{name}</h1>
          <p className="text-sm text-fg-muted mt-1.5">
            Every job, Sunday through Saturday.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface-sunken p-1">
          <Button variant="ghost" size="sm" aria-label="Previous week"
            data-testid="officer-week-prev"
            onClick={() => navigate(`/scheduling/officer/${employeeId}/${shiftWeeks(weekStart, -1)}`)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-fg px-2 tabular-nums" data-testid="officer-week-label">
            {weekStart}
          </span>
          <Button variant="ghost" size="sm" aria-label="Next week"
            data-testid="officer-week-next"
            onClick={() => navigate(`/scheduling/officer/${employeeId}/${shiftWeeks(weekStart, 1)}`)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-fg-muted" data-testid="officer-week-loading">Loading…</p>
      ) : error ? (
        <div className="rounded-xl border border-line bg-surface-sunken px-6 py-10 text-center"
          data-testid="officer-week-error">
          <p className="text-base md:text-lg text-fg">{error}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3" data-testid="officer-week-totals">
            <Badge variant="outline" className="tabular-nums">
              <Clock className="h-3 w-3 mr-1.5" /> {data.total_hours}h this week
            </Badge>
            {data.over_threshold ? (
              <Badge variant="outline" className="border-warning-border text-warning tabular-nums"
                data-testid="officer-week-over">
                {data.overtime_hours}h overtime · over {data.threshold_hours}
              </Badge>
            ) : (
              <span className="text-sm text-fg-muted tabular-nums">
                {data.hours_remaining}h left under {data.threshold_hours}
              </span>
            )}
            {(data.jobs_this_week || []).length > 0 && (
              <span className="text-sm text-fg-muted">
                Jobs: {data.jobs_this_week.join(', ')}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {Object.entries(data.days || {}).map(([date, rows], i) => (
              <div key={date} className="rounded-lg border border-line bg-surface-sunken p-3"
                data-testid={`officer-week-day-${date}`}>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium text-fg">{DAY_LABELS[i]}</span>
                  <span className="text-xs text-fg-muted tabular-nums">{date.slice(5)}</span>
                </div>
                <div className="mt-2.5 space-y-2">
                  {!rows.length ? <p className="text-xs text-fg-muted/70">—</p> : rows.map((s) => (
                    <div key={s.id} className="rounded-md border border-line bg-surface p-2"
                      data-testid={`officer-week-shift-${s.id}`}>
                      <p className="text-xs font-medium text-fg tabular-nums flex items-center gap-1">
                        {hhmm(s.time_in)}–{hhmm(s.time_out)}
                        {s.next_day && <Moon className="h-3 w-3 text-fg-muted" />}
                      </p>
                      <p className="text-xs text-fg-muted mt-0.5 truncate">
                        {s.job_in_view ? (s.job_number || '—') : 'Other work'}
                        {s.post_name ? ` · ${s.post_name}` : ''}
                      </p>
                      <p className="text-[11px] text-fg-muted mt-0.5 tabular-nums">
                        {s.hours_calculated ?? 0}h · running {s.running_hours_after_this_shift}h
                        {s.overtime_hours > 0 ? ` · OT ${s.overtime_hours}h` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
